/**
 * Screen Key Utilities
 *
 * Manages screen keys for cache differentiation in navigation.
 *
 * Key Concepts:
 * - screenKey: Unique identifier for each navigation instance
 * - cacheKey: Composite key (pathname + screenKey) for route caching
 *
 * Usage:
 * - ALL routes now use screen keys for consistent caching behavior
 * - PUSH navigation: Generate new screenKey → fresh navigation instance
 * - POP/FORWARD/Direct access: Reuse existing screenKey → cached instance
 * - Main navigation routes (persistCache: true): Never removed from cache
 * - Detail routes (persistCache: false): Removed from cache on POP
 */

/**
 * Generate a random screen key
 * Format: {prefix}-{random}
 * Example: user-details-a3f9k2x7
 */
export const generateScreenKey = (prefix: string): string => {
    const randomPart = Math.random().toString(36).substr(2, 9);
    const screenKey = `${prefix}-${randomPart}`;
    // console.log('****************************** Generated screenKey:', screenKey);
    return screenKey;
};

/**
 * Get screen key prefix based on pathname pattern
 * Used when generating screen keys in RouteStack for direct navigation
 *
 * @param pathname - Current pathname (e.g., '/abc123', '/@john', '/abc123/videos')
 * @returns Screen key prefix (e.g., 'user-details', 'user-videos')
 *
 * @example
 * getScreenKeyPrefix('/abc123') → 'user-details'
 * getScreenKeyPrefix('/@john') → 'user-details'
 * getScreenKeyPrefix('/abc123/videos') → 'user-videos'
 * getScreenKeyPrefix('/') → 'feed'
 * getScreenKeyPrefix('/inbox') → 'inbox'
 */
export const getScreenKeyPrefix = (pathname: string): string => {
    // Main navigation routes
    if (pathname === '/') return 'feed';
    if (pathname === '/inbox') return 'inbox';
    if (pathname === '/more') return 'more';

    // Detail routes (specific static paths first, dynamic catch-all last)
    if (pathname.match(/^\/content-tags\/[^/]+$/)) return 'content-tags';
    if (pathname.match(/^\/content\/[^/]+$/)) return 'content-details';
    // `/default-media/:id` — web-only full-viewport viewer for default-bucket
    // media (authenticated but unencrypted, not public). Must precede the
    // `/[^/]+` user-details catch-all so the literal `default-media/...` prefix
    // is not classified as a user id.
    if (pathname.match(/^\/default-media\/[^/]+$/)) return 'default-media';
    // `/chat/media/:id` — the directly-routable chat-media viewer (`:id` is the
    // chat message `documentId`). Must precede the `/chat/:peerId` branch (a
    // bare `^/chat/[^/]+$` does NOT match the two-segment `chat/media/<id>`, but
    // ordering it first keeps the precedence intent explicit) and the
    // `/[^/]+/videos` two-segment catch-all so it is never classified as
    // `<user>/videos`.
    if (pathname.match(/^\/chat\/media\/[^/]+$/)) return 'chat-media';
    // `/video-call/:id` — the 1:1 chat video-call screen (`:id` is the call
    // session `callMessageId`). Must precede the `/[^/]+` user-details catch-all
    // so the literal `video-call/...` prefix is not classified as a user id.
    if (pathname.match(/^\/video-call\/[^/]+$/)) return 'video-call';
    // `/live/:id` — the community live-session screen (`:id` is the live message
    // `liveStreamingMessageId`). Must precede the `/[^/]+` user-details catch-all
    // so the literal `live/...` prefix is not classified as a user id.
    if (pathname.match(/^\/live\/[^/]+$/)) return 'live-streaming';
    // `/chat/:peerId` — must precede the `/[^/]+` user-details catch-all so
    // the literal `chat/...` prefix is not classified as a user id.
    if (pathname.match(/^\/chat\/[^/]+$/)) return 'chat';
    // `/community/media/:id` — the directly-routable community-media (video)
    // player (`:id` is the community message document id). Must precede the
    // `/community/:userId` branch below: the single-segment `^/community/[^/]+$`
    // regex does NOT match this two-segment media path, so without this branch
    // the new page falls through to the catch-all and mis-keys the
    // screen-key / keep-alive cache (mirrors the `chat-media` precedence above).
    if (pathname.match(/^\/community\/media\/[^/]+$/)) return 'community-media';
    // `/community/:userId` — must precede the `/[^/]+` user-details catch-all so
    // the literal `community/...` prefix is not classified as a user id.
    if (pathname.match(/^\/community\/[^/]+$/)) return 'community';
    // `/admin/users-feedback/:id` — admin users-feedback details.
    if (pathname.match(/^\/admin\/users-feedback\/[^/]+$/)) return 'feedback-details';
    // `/admin/users-feedback` — admin users-feedback list.
    if (pathname.match(/^\/admin\/users-feedback$/)) return 'feedback';
    if (pathname.match(/^\/profile\/edit$/)) return 'edit-user-profile';
    if (pathname.match(/^\/premium-packages$/)) return 'premium-packages';
    if (pathname.match(/^\/campaigns$/)) return 'campaigns';
    // `/feedback` — the "add feedback" write page (`FeedbackPage`). Distinct
    // `feedback-add` prefix: the `/admin/users-feedback` admin list above already
    // owns `'feedback'`. The anchored `^/feedback$` cannot match the multi-segment
    // `/admin/users-feedback*` paths, so those branches stay unaffected; it must
    // precede the `/[^/]+` user-details catch-all below so `feedback` isn't
    // classified as a user id.
    if (pathname.match(/^\/feedback$/)) return 'feedback-add';
    // `admin/latest-users` — two-segment static path; must precede the
    // `/[^/]+/[^/]+` user catch-alls below so it isn't misclassified.
    if (pathname.match(/^\/admin\/latest-users$/)) return 'latest-users';
    if (pathname.match(/^\/wallet$/)) return 'wallet';
    if (pathname.match(/^\/contact$/)) return 'contact';
    // `/about` — the public About Expause page (single static segment). Must
    // precede the `/[^/]+` user-details catch-all so it is not classified as a
    // user id (same precedence concern as `/contact` / `/support`).
    if (pathname.match(/^\/about$/)) return 'about';
    // `/delete-account` — the public account-deletion page (single static
    // segment). Must precede the `/[^/]+` user-details catch-all so it is not
    // classified as a user id (same precedence concern as `/about` /
    // `/contact` / `/support`).
    if (pathname.match(/^\/delete-account$/)) return 'delete-account';
    // `/login` — the unauthenticated entry page (`LoginPage`). Unlike every other
    // entry here, this branch is NOT reached from `KeepAliveOutlet`: `AppRoutes.tsx`
    // declares `/login` as a top-level `<Route>` sibling of the
    // `ProtectedRoutesWrapper` branch that renders the outlet, so the outlet never
    // sees this pathname and never mints a route-cache key for it. `LoginPage`
    // calls `getScreenKeyPrefix` itself and mints its own key from the result —
    // which is why the branch must still precede the `/[^/]+` user-details
    // catch-all (same precedence concern as `/about` / `/contact` /
    // `/delete-account`), or the login key would land in the `user-details`
    // namespace.
    if (pathname.match(/^\/login$/)) return 'login';
    if (pathname === '/mock-chat') return 'mock-chat';
    if (pathname.match(/^\/unlocked$/)) return 'unlocked';
    if (pathname.match(/^\/liked$/)) return 'liked';
    if (pathname.match(/^\/watch-later$/)) return 'watch-later';
    // `/ideas/add` (Add idea) — must precede the `/^\/ideas$/` branch so the
    // two-segment add route is not shadowed (and the `/[^/]+` user-details
    // catch-all does not misclassify it).
    if (pathname.match(/^\/ideas\/add$/)) return 'add-roadmap';
    // `/ideas` (Roadmap) — single static segment; must precede the
    // `/[^/]+` user-details catch-all or it is misclassified as a user id.
    if (pathname.match(/^\/ideas$/)) return 'ideas';
    if (pathname.match(/^\/subscriptions$/)) return 'subscriptions';
    if (pathname.match(/^\/subscribers$/)) return 'subscribers';
    if (pathname.match(/^\/saved-users$/)) return 'saved-users';
    if (pathname.match(/^\/settings\/[^/]+$/)) return 'settings-category';
    if (pathname.match(/^\/settings$/)) return 'settings';
    if (pathname.match(/^\/search$/)) return 'search';
    // Legal public routes (must precede the `/[^/]+` user-details catch-all).
    // Namespaced under `/legal/*` so they cannot collide with the user-details
    // catch-all that matches `/[^/]+` at the URL root.
    if (pathname === '/legal') return 'legal-documents';
    if (pathname === '/legal/terms-of-use') return 'terms-of-use';
    if (pathname === '/legal/coins-policy') return 'coins-policy';
    if (pathname === '/legal/rewards-policy') return 'rewards-policy';
    if (pathname === '/legal/exclusive-subscriptions-policy') return 'exclusive-subscriptions-policy';
    if (pathname === '/legal/premium-subscriptions-policy') return 'premium-subscriptions-policy';
    if (pathname === '/legal/privacy-policy') return 'privacy-policy';
    if (pathname === '/legal/community-guidelines') return 'community-guidelines';
    // Support public routes (must precede the `/[^/]+` user-details catch-all
    // and the `/[^/]+/[^/]+` two-segment catch-all so `/support` and
    // `/support/:id` don't get swallowed as a user id or `<user>/videos`).
    if (pathname === '/support') return 'support';
    if (pathname.match(/^\/support\/[^/]+$/)) return 'support-item';
    // User routes last (dynamic catch-all patterns)
    if (pathname.match(/^\/[^/]+\/videos$/)) return 'user-videos';
    if (pathname.match(/^\/[^/]+$/)) return 'user-details';

    // Fallback: use pathname as prefix (sanitized)
    return pathname.replace(/[^a-zA-Z0-9]/g, '-').replace(/^-+|-+$/g, '');
};

/**
 * Compose a cache key from pathname and optional screenKey
 * 
 * @param pathname - Route pathname (e.g., '/abc123')
 * @param screenKey - Optional screen key for cache differentiation
 * @returns Cache key (e.g., '/abc123' or '/abc123#screen-abc123')
 */
export const composeCacheKey = (pathname: string, screenKey?: string | null): string => {
    if (!screenKey) {
        return pathname;
    }
    return `${pathname}#${screenKey}`;
};

/**
 * Extract screenKey from location state
 * 
 * @param state - Location state object
 * @returns screenKey if present, undefined otherwise
 */
export const extractScreenKey = (state: any): string | undefined => {
    return state?.screenKey;
};

/**
 * Parse a cache key into pathname and screenKey components
 * 
 * @param cacheKey - Composite cache key
 * @returns Object with pathname and optional screenKey
 */
export const parseCacheKey = (cacheKey: string): { pathname: string; screenKey?: string } => {
    const parts = cacheKey.split('#');
    if (parts.length === 1) {
        return { pathname: parts[0] };
    }
    return {
        pathname: parts[0],
        screenKey: parts[1],
    };
};

/**
 * Check if a pathname corresponds to a main navigation route
 * Main navigation routes (/, /inbox, /more) use screen keys and persist in cache
 *
 * @param pathname - Route pathname
 * @returns true if this is a main navigation route
 */
export const isMainNavigationRoute = (pathname: string): boolean => {
    // Main navigation routes that persist in cache (never removed)
    const mainRoutes = ['/', '/inbox', '/more'];
    return mainRoutes.includes(pathname);
};
